import { createTheme, Fade } from "@material-ui/core";
import React from "react";

import CheckboxIcon from "../components/CheckboxIcon";

export const BRAND_PRIMARY = "#1789c9";
export const BRAND_PRIMARY_TRANS_02 = "rgb(235 0 0 / 20%)";

const colors = {
  black: "#000",
  brandPrimary: BRAND_PRIMARY,
  gray: "#767676",
  grayAltLight: "#e8e8e8",
  grayDark: "#414141",
  grayLight: "rgba(239, 239, 239, 1)", // #efefef
  red: "rgba(235, 0, 0, 0.7)",
  redLight: "rgba(235, 0, 0, 0.3)",
};

const theme = createTheme({
  colors,
  overrides: {
    MuiAutocomplete: {
      endAdornment: { top: 5 },
      input: {
        padding: "12px 4px !important",
      },
      inputRoot: {
        '&.MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
          padding: "0 9px",
        },
      },
      listbox: {
        maxHeight: 250,
        padding: 0,
      },
      option: {
        "&[aria-selected='true']": {
          backgroundColor: "white",
        },
        "&[data-focus='true']": {
          backgroundColor: colors.grayLight,
        },
        backgroundColor: "white",
        minHeight: "40px !important",
        paddingBottom: 5,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 5,
      },
      paper: {
        "&:empty": {
          border: "none",
        },
        border: `1px solid ${colors.brandPrimary}`,
        borderTop: 0,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        boxShadow: "none",
        margin: "-4px 0 0",
      },
    },
    MuiButton: {
      outlined: {
        borderColor: colors.grayLight,
        padding: "5px 10px",
      },
      root: {
        "& svg": {
          color: "inherit",
        },
        minHeight: 40,
        minWidth: "unset",
        padding: "5px 10px",
        textTransform: "none",
      },
    },
    MuiButtonBase: {
      root: {
        "&.Mui-focusVisible": {
          outline: `1px solid ${colors.brandPrimary}`,
        },
      },
    },
    MuiButtonGroup: {
      grouped: {
        border: `1px solid ${colors.grayLight}`,
      },
      root: { height: 40 },
    },
    MuiCheckbox: {
      colorSecondary: {
        "&:hover": {
          backgroundColor: "transparent !important",
        },
      },
      root: {
        "&.Mui-focusVisible": {
          "& div": {
            outline: `1px solid ${colors.brandPrimary}`,
          },
          outline: "none",
        },
      },
    },
    MuiCircularProgress: {
      colorSecondary: {
        color: colors.gray,
      },
      root: {
        "& svg": {
          margin: 0,
        },
      },
    },
    MuiFormControl: {
      root: {
        alignItems: "center",
        flexDirection: "row",
      },
    },
    MuiFormControlLabel: {
      root: {
        "&$disabled": {
          color: colors.grayDark,
          opacity: 0.38,
          pointerEvents: "none",
        },
      },
    },
    MuiInput: {
      inputMultiline: {
        resize: "vertical",
      },
    },
    MuiInputBase: {
      root: {
        "&$disabled": {
          color: colors.grayDark,
          opacity: 0.38,
        },
        "&$disabled:hover": {
          backgroundColor: "white",
          cursor: "not-allowed",
        },
      },
    },
    MuiInputLabel: {
      shrink: {
        transform: "translate(0, 0) scale(1)",
      },
    },
    MuiListItem: {
      button: {
        "&:hover": {
          backgroundColor: colors.grayLight,
        },
      },
      root: {
        "&:focus-visible": {
          outline: `1px solid ${colors.brandPrimary}`,
        },
        "&:hover": {
          backgroundColor: colors.grayLight,
        },
        backgroundColor: "white",
        border: `1px solid ${colors.grayLight}`,
        color: colors.grayDark,
        display: "flex",
        justifyContent: "space-between",
      },
    },
    MuiMenuItem: {
      root: {
        border: `1px solid ${colors.grayLight}`,
        minHeight: "48px !important",
        textAlign: "center",
        whiteSpace: "break-spaces",
      },
    },
    MuiOutlinedInput: {
      input: {
        padding: "12px 14px",
      },
      root: {
        "&:hover": {
          backgroundColor: colors.grayLight,
        },
        "&:hover $notchedOutline": {
          borderColor: colors.grayLight,
          borderWidth: 1,
        },
        "&$disabled $notchedOutline": {
          borderColor: colors.grayLight,
          opacity: 0.38,
        },
        "&$focused $notchedOutline": {
          borderColor: colors.brandPrimary,
          borderWidth: 1,
        },
        "& $notchedOutline": {
          borderColor: colors.grayLight,
        },
        borderRadius: 4,
        borderWidth: 1,
        boxSizing: "border-box",
      },
    },
    MuiSelect: {
      icon: {
        transition: "transform 400ms",
        width: 18,
      },
      root: {
        "&$disabled:hover": {
          cursor: "not-allowed",
        },
      },
    },
    MuiSnackbarContent: {
      root: {
        backgroundColor: "#fff",
        borderRadius: 0,
        color: colors.grayDark,
        minWidth: "0 !important",
      },
    },
    MuiTab: {
      root: {
        "&:hover": {
          backgroundColor: colors.grayLight,
        },
        "&$selected": {
          backgroundColor: "white",
          border: `1px solid ${colors.grayLight}`,
          borderBottom: "none",
          color: colors.brandPrimary,
          fontWeight: "bold",
        },
        "&$selected:hover": {
          backgroundColor: "white",
        },
        borderBottom: `1px solid ${colors.grayLight}`,
        minWidth: "70px!important",
        position: "relative",
        textTransform: "none",
      },
    },
    MuiTabs: {
      indicator: {
        display: "none",
      },
    },
    MuiToggleButton: {
      root: {
        "&.Mui-selected": {
          backgroundColor: colors.brandPrimary,
          color: "white",
        },
        "&.Mui-selected:hover": {
          backgroundColor: "#105f8c!important",
          color: "white",
        },
        color: colors.grayDark,

        fontWeight: "bold",
        textTransform: "none",
      },
    },
    MuiTypography: {
      body1: { whiteSpace: "pre-line" },
      h2: { margin: "12px 0" },
      h4: { margin: "8px 0" },
      paragraph: { margin: "8px 0" },
    },
    PrivateSwitchBase: {
      root: {
        padding: "6px 9px",
      },
    },
  },
  palette: {
    error: {
      light: colors.redLight,
      main: colors.red,
    },
    primary: {
      main: colors.brandPrimary,
    },
    secondary: {
      light: colors.grayLight,
      main: colors.grayDark,
      medium: colors.gray,
    },
  },
  props: {
    MuiButton: {
      disableRipple: true,
      variant: "outlined",
    },
    MuiButtonGroup: {
      disableRipple: true,
    },
    MuiCheckbox: {
      // @ts-expect-error - seems to work as expected in sidebar
      checkedIcon: <CheckboxIcon checked />,
      icon: <CheckboxIcon />,
      size: "small",
    },
    MuiCircularProgress: {
      size: 20,
    },
    MuiDialog: {
      maxWidth: false,
    },
    MuiIconButton: {
      disableRipple: true,
    },
    MuiListItem: {
      // @ts-expect-error - seems to work as expected in sidebar
      component: "button",
    },
    MuiMenu: {
      TransitionComponent: Fade,
    },
    MuiToggleButtonGroup: {
      size: "small",
    },
  },
  styles: {
    flexCenter: {
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
    },
    link: {
      "&:hover": {
        color: colors.brandPrimary,
      },
      color: colors.grayDark,
    },
  },
  typography: {
    body1: {
      color: colors.grayDark,
      fontSize: "1em",
      fontWeight: "normal",
      lineHeight: "19.5px",
    },
    button: {
      fontSize: "1em",
      fontWeight: "bold",
      letterSpacing: 0,
      lineHeight: "26px",
    },
    fontFamily: "Lato, sans-serif",
    fontSize: 15,
    h2: {
      color: colors.grayDark,
      fontSize: "1.5em",
      fontWeight: "bold",
      lineHeight: "22.5px",
    },
    h4: {
      color: colors.grayDark,
      fontSize: "1em",
      fontWeight: "bold",
      lineHeight: "22.5px",
    },
    htmlFontSize: 15,
    subtitle1: {
      color: "#959494",
      fontSize: 13,
      lineHeight: "19.5px",
    },
  },
});

export default theme;
